import React, { useState, useCallback } from 'react';
import { Card, Form, Input, InputNumber, Select, Button, theme } from 'antd';
import { DynamicFormCardProps, FormField } from './DynamicFormCard.utils';
import { useMeasurement } from '@/hooks/useMeasurement';
import './DynamicFormCard.css';
import { DUMMY_MODEL_IDS } from '@/comp/MapBuilder/nodes/nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';

const { useToken } = theme;

export const DynamicFormCard: React.FC<DynamicFormCardProps> = ({
  title,
  fields,
  onSubmit,
  onCancel,
  submitButtonText = 'Submit',
  initialValues,
  form: propForm,
  onValuesChange,
  isLoading = false,
}) => {
  const { token } = useToken();
  const { tunit } = useMeasurement();
  const [form] = Form.useForm(propForm);
  const [searchTexts, setSearchTexts] = useState<{ [key: string]: string }>({});
  const basicFields = fields.filter(field => !field.isAdvanced);

  const getFieldRules = (field: FormField) => {
    return field.rules || (field.required ? [{ required: true, message: `${field.label} is required` }] : []);
  };

  const handleSearch = useCallback(
    (fieldName: string) => (value: string) => {
      setSearchTexts(prev => ({ ...prev, [fieldName]: value }));
    },
    [],
  );

  const renderFormField = (field: FormField) => {
    switch (field.type) {
      case 'input':
        if (field.name === 'pipeDiameter') {
          return (
            <Form.Item key={field.name} label={field.label} name={field.name} rules={getFieldRules(field)}>
              <InputNumber
                placeholder={field.placeholder}
                suffix={<span>{tunit('mm')}</span>}
                style={{ width: '100%' }}
                min={0}
                precision={0}
              />
            </Form.Item>
          );
        }
        return (
          <Form.Item key={field.name} label={field.label} name={field.name} rules={getFieldRules(field)}>
            <Input placeholder={field.placeholder} />
          </Form.Item>
        );
      case 'select': {
        const currentSearchText = searchTexts[field.name] || '';
        const filteredOptions = field.options.filter(option =>
          option.label.toLowerCase().includes(currentSearchText.toLowerCase()),
        );

        let selectOptions = !isLoading ? filteredOptions : [];

        if (!isLoading && selectOptions.length === 0) {
          selectOptions = [{ value: DUMMY_MODEL_IDS[initialValues?.deviceType], label: 'No matching Spec' }];
        }

        return (
          <Form.Item key={field.name} label={field.label} name={field.name} rules={getFieldRules(field)}>
            <Select
              placeholder={field.placeholder}
              disabled={field.disabled}
              loading={field.loading || isLoading}
              onChange={value => field.type === 'select' && field.onChange?.(value, form)}
              showSearch
              onSearch={handleSearch(field.name)}
              filterOption={false}
              options={selectOptions}
            />
          </Form.Item>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Card
      className="dynamic-form-card"
      styles={{
        header: {
          padding: `${token.paddingXS}px ${token.paddingSM}px`,
        },
        body: {
          padding: '0px',
        },
      }}
      title={title}
    >
      <div className="form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={initialValues}
          onValuesChange={onValuesChange}
        >
          <div className="form-fields-container">
            {basicFields.map(renderFormField)}
            {/* {showAdvanced && advancedFields.map(renderFormField)} */}
          </div>
          <Form.Item className="button-container">
            <Button type="link" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" disabled={isLoading}>
              {submitButtonText}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Card>
  );
};
